import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import { Box, IconButton, Stack, Typography } from '@mui/material';
//
import Icon from '../Icon';
import { formControlsSx, secondaryTableSx } from '../Surface';
import {
  HIT,
  RADIUS,
  UNIVERSE_FONT,
  accentOf,
  focusRingSx,
  hairlineOf
} from 'src/theme/universe/brand';
import { textTabsSx } from 'src/theme/universe/surfaceSx';

// ---------------------------------------------------------------------------
// THE ANATOMY OF A DIALOG (DESIGN.md §16): `dialog-title` 18/700 with its
// meta ON THE SAME LINE, a hairline; the fields on the grid; a hairline;
// Cancel and the verb right-aligned. No icon tile, no coloured strip. The
// paper is radius 14 (`radius.modal`) and goes full-screen under `sm`. Three
// pieces: the head, the footer, and the paper recipe a dialog spreads on
// itself.
//
// ONE LINE, ONE FACE (the owner, 09/10/2026: "anche questo titolo mettilo su
// una riga tutto stesso font, limita lo scroll"). The meta used to be a 12px
// caption stacked under the title — "Find a linguist" over "Spanish →
// Italian" — which read as two headings and cost every dialog in the portal
// ~18px of height before its first field. Title and meta are now one line in
// the same size: the title in the ink, what it is about in the quiet ink
// after a dot. On a narrow dialog the meta wraps rather than clipping — the
// pair and the order number are not decoration.
// ---------------------------------------------------------------------------

const isLight = (theme) => theme.palette.mode === 'light';

DialogHead.propTypes = {
  title: PropTypes.node,
  meta: PropTypes.node,
  onClose: PropTypes.func,
  sx: PropTypes.object
};

export default function DialogHead({ title, meta, onClose, sx }) {
  const theme = useTheme();
  if (!title && !meta) return null;
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${hairlineOf(theme)}`,
        ...sx
      }}
    >
      <Typography
        component="h2"
        sx={{
          m: 0,
          minWidth: 0,
          flexGrow: 1,
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          columnGap: 1,
          fontFamily: UNIVERSE_FONT,
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1.35,
          letterSpacing: 0,
          color: 'text.primary'
        }}
      >
        {title}
        {Boolean(title) && Boolean(meta) && (
          <Box component="span" aria-hidden sx={{ color: 'text.disabled', fontWeight: 400 }}>
            ·
          </Box>
        )}
        {meta && (
          <Box
            component="span"
            sx={{ minWidth: 0, fontWeight: 500, color: 'text.secondary', overflowWrap: 'anywhere' }}
          >
            {meta}
          </Box>
        )}
      </Typography>
      {onClose && (
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            flex: 'none',
            mr: -1,
            width: 36,
            height: 36,
            borderRadius: `${RADIUS.small}px`,
            color: 'text.secondary',
            '&:hover': { color: accentOf(theme) },
            '&:focus-visible': focusRingSx(theme),
            '@media (pointer: coarse)': { width: HIT.touch, height: HIT.touch }
          }}
        >
          <Icon name="CloseCircle" size="md" />
        </IconButton>
      )}
    </Stack>
  );
}

DialogFooter.propTypes = {
  children: PropTypes.node,
  hint: PropTypes.node,
  sx: PropTypes.object
};

/** A hairline, the hint at the left in meta, Cancel + the verb at the right. */
export function DialogFooter({ children, hint, sx }) {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      spacing={1.5}
      useFlexGap
      flexWrap="wrap"
      sx={{
        px: 3,
        py: 2,
        borderTop: `1px solid ${hairlineOf(theme)}`,
        ...sx
      }}
    >
      {hint && (
        <Typography
          component="div"
          sx={{
            mr: 'auto',
            minWidth: 0,
            fontFamily: UNIVERSE_FONT,
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1.4,
            color: 'text.secondary'
          }}
        >
          {hint}
        </Typography>
      )}
      {children}
    </Stack>
  );
}

/**
 * THE SCRIM BEHIND A LAYER (DESIGN.md §7): a flat wash, not a gradient.
 * `theme/overrides/Backdrop.js` is still the template's 75deg gradient that
 * reaches FULL opacity at its far end, so one side of every dialog goes
 * black while the other stays half-lit. Spread on a dialog's
 * `BackdropProps` until that override is replaced for all ~40 dialogs
 * (phase 0 work; reported, not done from one page).
 */
export const dialogBackdropProps = {
  sx: { background: 'rgba(6,23,54,.56)' }
};

/**
 * The paper of a dialog: radius 14, the UI face on everything inside it, the
 * stratum-4 shadow, a lit edge on the night; the fields inside it on the
 * form geometry and the quiet head on any table it draws. Spread on
 * DialogAnimate's `sx` (which reaches the Paper) or on a MUI Dialog's paper.
 */
export const dialogPaperSx = (theme) => ({
  bgcolor: 'background.paper',
  backgroundImage: 'none',
  borderRadius: `${RADIUS.modal}px`,
  '&.MuiPaper-rounded.MuiDialog-paper': { borderRadius: `${RADIUS.modal}px` },
  '&.MuiDialog-paperFullScreen, &.MuiDialog-paperFullScreen.MuiPaper-rounded': {
    borderRadius: 0
  },
  border: isLight(theme) ? 0 : '1px solid rgba(255,255,255,0.08)',
  boxShadow: isLight(theme)
    ? '0 24px 64px -12px rgba(1,38,109,.32)'
    : '0 32px 80px -16px rgba(0,0,0,.7), inset 0 1px 0 rgba(255,255,255,.08)',
  fontFamily: UNIVERSE_FONT,
  '& .MuiTypography-root, & .MuiButton-root, & .MuiTab-root, & .MuiInputBase-root, & .MuiFormControlLabel-label, & .MuiMenuItem-root, & .MuiFormLabel-root, & .MuiFormHelperText-root, & .MuiTableCell-root, & .MuiChip-label, & .MuiAlert-message, & .MuiLink-root':
    { fontFamily: UNIVERSE_FONT },
  // MUI's own title and actions, where a dialog still draws them
  '& .MuiDialogTitle-root': {
    px: 3,
    pt: 2.5,
    pb: 2,
    fontFamily: UNIVERSE_FONT,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.3,
    letterSpacing: 0,
    borderBottom: `1px solid ${hairlineOf(theme)}`
  },
  '& .MuiDialogContent-root': { px: 3, py: 2.5 },
  '& .MuiDialogActions-root': {
    px: 3,
    py: 2,
    borderTop: `1px solid ${hairlineOf(theme)}`
  },
  '& .MuiDialogContent-root + .MuiDialogActions-root': {
    borderTop: `1px solid ${hairlineOf(theme)}`
  },
  // the alerts in the system's geometry
  '& .MuiAlert-root': {
    borderRadius: `${RADIUS.small}px`,
    fontFamily: UNIVERSE_FONT,
    fontSize: 13,
    '& .MuiAlert-icon': { alignItems: 'center' }
  },
  '& .MuiTabs-root': textTabsSx(theme),
  '& .MuiTable-root': secondaryTableSx(theme),
  ...formControlsSx(theme)
});
